/**
 * Users Service
 * 
 * Chức năng: Service để gọi API liên quan đến nhân sự (users)
 * Dùng để: 
 * - CRUD operations cho users
 * - Tập trung logic gọi API users
 * - Dùng chung cho mobile và desktop app
 */
import {
  BaseApiService,
  GENERIC_API_ERROR_MESSAGE,
  getUserFriendlyErrorMessage,
} from './base-api.service';
import { API_ENDPOINTS } from '../constants';
import {
  UserDetail,
  CreateUserDto,
  UpdateUserDto,
  BlockUserDto,
  UpdateUserSalaryDto,
  UserListResponse,
  GetUsersParams,
  GetDeletedUsersParams,
} from '../types';

export interface UserUpdateRequest {
  id: number;
  userId: number;
  updateData: any;
  previousData?: any;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  approvedById?: number;
  rejectedById?: number;
  rejectionReason?: string;
  approver?: {
    id: number;
    fullName: string;
    avatar?: string;
  };
  rejector?: {
    id: number;
    fullName: string;
    avatar?: string;
  };
}

export interface GetUserUpdateRequestsParams {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'rejected';
  userId?: number;
}

export interface UserUpdateRequestsResponse {
  data: UserUpdateRequest[];
  total: number;
  page: number;
  limit: number;
}

export abstract class UsersService extends BaseApiService {
  /**
   * Lấy thông tin profile của user hiện tại
   */
  async getProfile(): Promise<UserDetail> {
    return this.get<UserDetail>(API_ENDPOINTS.USERS.PROFILE);
  }

  /**
   * Lấy danh sách users
   */
  async getUsers(params?: GetUsersParams): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.role) {
      queryParams.append('role', params.role);
    }
    if (params?.departmentId) {
      queryParams.append('departmentId', params.departmentId.toString());
    }
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }
    if (params?.isBlocked !== undefined) {
      queryParams.append('isBlocked', params.isBlocked.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${API_ENDPOINTS.USERS.LIST}?${queryString}`
      : API_ENDPOINTS.USERS.LIST;

    return this.get<UserListResponse>(endpoint);
  }

  /**
   * Lấy danh sách users đã xóa mềm
   */
  async getDeletedUsers(
    params?: GetDeletedUsersParams
  ): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${API_ENDPOINTS.USERS.LIST}/deleted?${queryString}`
      : `${API_ENDPOINTS.USERS.LIST}/deleted`;

    return this.get<UserListResponse>(endpoint);
  }

  /**
   * Lấy chi tiết user theo ID
   */
  async getUserById(id: number): Promise<UserDetail> {
    return this.get<UserDetail>(API_ENDPOINTS.USERS.DETAIL(id.toString()));
  }

  /**
   * Lấy chi tiết user theo mã nhân viên chính xác
   */
  async getUserByEmployeeCode(employeeCode: string): Promise<UserDetail> {
    return this.get<UserDetail>(
      API_ENDPOINTS.USERS.BY_EMPLOYEE_CODE(employeeCode.trim())
    );
  }

  /**
   * Tạo user mới
   */
  async createUser(data: CreateUserDto): Promise<{
    success: boolean;
    message: string;
    data: UserDetail;
  }> {
    return this.post<{
      success: boolean;
      message: string;
      data: UserDetail;
    }>(API_ENDPOINTS.USERS.CREATE, data);
  }

  /**
   * Cập nhật thông tin user
   */
  async updateUser(
    id: number,
    data: UpdateUserDto
  ): Promise<{
    success: boolean;
    message: string;
    data?: UserDetail;
    requestId?: number;
    status?: string;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data?: UserDetail;
      requestId?: number;
      status?: string;
    }>(API_ENDPOINTS.USERS.UPDATE(id.toString()), data);
  }

  /**
   * Khóa user
   */
  async blockUser(
    id: number,
    data: BlockUserDto
  ): Promise<{
    success: boolean;
    message: string;
    data: UserDetail;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data: UserDetail;
    }>(`${API_ENDPOINTS.USERS.DETAIL(id.toString())}/block`, data);
  }

  /**
   * Mở khóa user
   */
  async unblockUser(
    id: number
  ): Promise<{
    success: boolean;
    message: string;
    data: UserDetail;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data: UserDetail;
    }>(`${API_ENDPOINTS.USERS.DETAIL(id.toString())}/unblock`);
  }

  /**
   * Khôi phục user đã xóa mềm
   */
  async restoreUser(
    id: number
  ): Promise<{
    success: boolean;
    message: string;
    data: UserDetail;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data: UserDetail;
    }>(`${API_ENDPOINTS.USERS.DETAIL(id.toString())}/restore`);
  }

  /**
   * Xóa user (xóa mềm)
   */
  async deleteUser(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.delete<{
      success: boolean;
      message: string;
    }>(API_ENDPOINTS.USERS.DELETE(id.toString()));
  }

  /**
   * Xóa vĩnh viễn user
   */
  async permanentDeleteUser(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.delete<{
      success: boolean;
      message: string;
    }>(`${API_ENDPOINTS.USERS.DELETE(id.toString())}/permanent`);
  }

  /**
   * Cập nhật lương cho nhân viên
   */
  async updateUserSalary(
    id: number,
    data: UpdateUserSalaryDto
  ): Promise<{
    success: boolean;
    message: string;
    data: UserDetail;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data: UserDetail;
    }>(`${API_ENDPOINTS.USERS.DETAIL(id.toString())}/salary`, data);
  }

  /**
   * Upload avatar cho user
   */
  async uploadAvatar(
    id: number,
    file: File
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      url: string;
      relativeUrl: string;
      fileName: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fullUrl = `${this.getBaseURL()}${API_ENDPOINTS.USERS.DETAIL(id.toString())}/avatar`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        getUserFriendlyErrorMessage(errorData.message, GENERIC_API_ERROR_MESSAGE),
      );
    }

    return response.json();
  }

  /**
   * Upload ảnh lá số tử vi cho user hiện tại
   */
  async uploadHoroscope(
    file: File
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      url: string;
      relativeUrl: string;
      fileName: string;
      fileSize: number;
      fileType: string;
      fileExtension: string;
      originalName: string;
      checksum: string;
      id: number;
      uploadedAt: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fullUrl = `${this.getBaseURL()}${API_ENDPOINTS.UPLOAD.TUVI}`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        getUserFriendlyErrorMessage(errorData.message, GENERIC_API_ERROR_MESSAGE),
      );
    }

    return response.json();
  }

  /**
   * Upload personal avatar cho user
   */
  async uploadPersonalAvatar(
    id: number,
    file: File
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      url: string;
      relativeUrl: string;
      fileName: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fullUrl = `${this.getBaseURL()}${API_ENDPOINTS.USERS.DETAIL(id.toString())}/personal-avatar`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        getUserFriendlyErrorMessage(errorData.message, GENERIC_API_ERROR_MESSAGE),
      );
    }

    return response.json();
  }

  /**
   * Upload ảnh CCCD/CMND/Passport (giấy tờ tùy thân) cho user hiện tại
   * Endpoint backend: POST /upload/id-card?documentType=CCCD&side=front
   */
  async uploadIdCard(
    file: File,
    documentType: string,
    side: 'front' | 'back' = 'front',
    userId?: number,
  ): Promise<{ 
    success: boolean;
    message: string;
    data: {
      url: string;
      relativeUrl: string;
      fileName: string;
      fileSize: number;
      fileType: string;
      fileExtension: string;
      originalName: string;
      checksum: string;
      id: number;
      uploadedAt: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const query = new URLSearchParams({
      documentType,
      side,
    });

    if (userId) {
      query.append('userId', userId.toString());
    }

    const fullUrl = `${this.getBaseURL()}/upload/id-card?${query.toString()}`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        getUserFriendlyErrorMessage(errorData.message, GENERIC_API_ERROR_MESSAGE),
      );
    }

    return response.json();
  }

  async uploadCertificateAttachment(
    userId: number,
    certificateId: number,
    file: File,
  ): Promise<{
    success: boolean;
    message: string;
    requestId?: number;
    status?: string;
    data: {
      id?: number;
      certificateId: number;
      fileName: string;
      fileUrl: string;
      fileSize?: number;
      uploadedAt?: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fullUrl = `${this.getBaseURL()}${API_ENDPOINTS.USERS.DETAIL(userId.toString())}/certificates/${certificateId}/attachments`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        getUserFriendlyErrorMessage(errorData.message, GENERIC_API_ERROR_MESSAGE),
      );
    }

    return response.json();
  }

  async removePendingCertificateAttachment(userId: number, certificateId: number, fileUrl: string): Promise<{ success: boolean; requestId?: number; message: string }> {
    const base = `${API_ENDPOINTS.USERS.DETAIL(userId.toString())}/certificates/${certificateId}/attachments/pending`;
    return this.delete(`${base}?fileUrl=${encodeURIComponent(fileUrl)}`);
  }

  async removeCertificateAttachment(userId: number, certificateId: number, attachmentId: number): Promise<{ success: boolean; requestId?: number; message: string }> {
    return this.delete(`${API_ENDPOINTS.USERS.DETAIL(userId.toString())}/certificates/${certificateId}/attachments/${attachmentId}`);
  }

  /**
   * Lấy danh sách yêu cầu cập nhật thông tin user
   */
  async getUpdateRequests(params?: GetUserUpdateRequestsParams): Promise<UserUpdateRequestsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.userId) queryParams.append('userId', params.userId.toString());

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${API_ENDPOINTS.USER_UPDATE_REQUESTS.LIST}?${queryString}`
      : API_ENDPOINTS.USER_UPDATE_REQUESTS.LIST;

    return this.get<UserUpdateRequestsResponse>(endpoint);
  }

  /**
   * Lấy chi tiết yêu cầu cập nhật theo ID
   */
  async getUpdateRequestById(id: number): Promise<UserUpdateRequest> {
    return this.get<UserUpdateRequest>(API_ENDPOINTS.USER_UPDATE_REQUESTS.DETAIL(id.toString()));
  }

  /**
   * Hủy yêu cầu cập nhật
   */
  async cancelUpdateRequest(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.delete<{
      success: boolean;
      message: string;
    }>(API_ENDPOINTS.USER_UPDATE_REQUESTS.DETAIL(id.toString()));
  }

  /**
   * Phê duyệt yêu cầu cập nhật
   */
  async approveUpdateRequest(id: number, options?: { confirmClearingCollections?: boolean }): Promise<{
    success: boolean;
    message: string;
    data: UserUpdateRequest;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data: UserUpdateRequest;
    }>(API_ENDPOINTS.USER_UPDATE_REQUESTS.APPROVE(id.toString()), options);
  }

  /**
   * Từ chối yêu cầu cập nhật
   */
  async rejectUpdateRequest(id: number, reason: string): Promise<{
    success: boolean;
    message: string;
    data: UserUpdateRequest;
  }> {
    return this.patch<{
      success: boolean;
      message: string;
      data: UserUpdateRequest;
    }>(API_ENDPOINTS.USER_UPDATE_REQUESTS.REJECT(id.toString()), { reason });
  }

  /**
   * Lấy lịch sử học vấn của user
   */
  async getEducationHistory(userId: number): Promise<Array<{
    id: number;
    fromDate?: string;
    toDate?: string;
    trainingForm?: string;
    educationLevel?: string;
    major?: string;
    trainingInstitution?: string;
  }>> {
    return this.get<Array<{
      id: number;
      fromDate?: string;
      toDate?: string;
      trainingForm?: string;
      educationLevel?: string;
      major?: string;
      trainingInstitution?: string;
    }>>(API_ENDPOINTS.USERS.EDUCATION_HISTORY(userId.toString()));
  }

  /**
   * Lấy kinh nghiệm làm việc của user
   */
  async getWorkExperience(userId: number): Promise<Array<{
    id: number;
    fromDate?: string;
    toDate?: string;
    company?: string;
    department?: string;
    position?: string;
    jobLocation?: string;
    workReferencePerson?: string;
    salary?: number;
  }>> {
    return this.get<Array<{
      id: number;
      fromDate?: string;
      toDate?: string;
      company?: string;
      department?: string;
      position?: string;
      jobLocation?: string;
      workReferencePerson?: string;
      salary?: number;
    }>>(API_ENDPOINTS.USERS.WORK_EXPERIENCE(userId.toString()));
  }

  /**
   * Lấy danh sách thành viên gia đình của user
   */
  async getFamilyMembers(userId: number): Promise<Array<{
    id: number;
    relationship?: string;
    fullName?: string;
    birthYear?: number;
    occupation?: string;
    workplace?: string;
    phone?: string;
    address?: string;
  }>> {
    return this.get<Array<{
      id: number;
      relationship?: string;
      fullName?: string;
      birthYear?: number;
      occupation?: string;
      workplace?: string;
      phone?: string;
      address?: string;
    }>>(API_ENDPOINTS.USERS.FAMILY_MEMBERS(userId.toString()));
  }

  /**
   * Lấy danh sách chứng chỉ của user
   */
  async getCertificates(userId: number): Promise<Array<{
    id: number;
    certificateNumber?: string;
    issuingUnit?: string;
    certificateType?: string;
    fromDate?: string;
    toDate?: string;
    notes?: string;
    attachments?: Array<{ id: number; certificateId: number; fileName: string; fileUrl: string; fileSize?: number }>;
  }>> {
    return this.get<Array<{
      id: number;
      certificateNumber?: string;
      issuingUnit?: string;
      certificateType?: string;
      fromDate?: string;
      toDate?: string;
      notes?: string;
      attachments?: Array<{ id: number; certificateId: number; fileName: string; fileUrl: string; fileSize?: number }>;
    }>>(API_ENDPOINTS.USERS.CERTIFICATES(userId.toString()));
  }

  /**
   * Lấy lịch sử đảng viên của user
   */
  async getPartyHistory(userId: number): Promise<Array<{
    id: number;
    cardNumber?: string;
    form?: string;
    fromDate?: string;
    toDate?: string;
    admissionPlace?: string;
    transferPlace?: string;
  }>> {
    return this.get<Array<{
      id: number;
      cardNumber?: string;
      form?: string;
      fromDate?: string;
      toDate?: string;
      admissionPlace?: string;
      transferPlace?: string;
    }>>(API_ENDPOINTS.USERS.PARTY_HISTORY(userId.toString()));
  }

  /**
   * Lấy thẻ định danh (ID cards) của user
   */
  async getIdCards(userId: number): Promise<Array<{
    id: number;
    documentType: string;
    fileName?: string;
    fileUrl?: string;
    status?: string;
  }>> {
    return this.get<Array<{
      id: number;
      documentType: string;
      fileName?: string;
      fileUrl?: string;
      status?: string;
    }>>(API_ENDPOINTS.USERS.ID_CARDS(userId.toString()));
  }

  /**
   * Lấy danh sách file cá nhân của user
   */
  async getPersonalFiles(userId: number): Promise<Array<{
    id: number;
    fileName: string;
    fileUrl: string;
    fileSize?: number;
    fileType?: string;
    fileExtension?: string;
    description?: string;
    uploadedAt?: string;
  }>> {
    return this.get<Array<{
      id: number;
      fileName: string;
      fileUrl: string;
      fileSize?: number;
      fileType?: string;
      fileExtension?: string;
      description?: string;
      uploadedAt?: string;
    }>>(API_ENDPOINTS.USERS.PERSONAL_FILES(userId.toString()));
  }

  /**
   * Lấy ảnh lá số tử vi của user
   */
  async getHoroscope(userId: number): Promise<{
    id: number;
    imageUrl: string;
    originalFormat?: string;
    uploadedAt?: string;
  } | null> {
    try {
      return await this.get<{
        id: number;
        imageUrl: string;
        originalFormat?: string;
        uploadedAt?: string;
      }>(API_ENDPOINTS.USERS.HOROSCOPE(userId.toString()));
    } catch (error) {
      // Nếu không có dữ liệu, trả về null
      return null;
    }
  }
}
